import { useState, useMemo } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Users, Building2, MessageCircle } from 'lucide-react';
import { useContactsForChat, ChatContact } from '@/hooks/useContactsForChat';
import { useLanguage } from '@/contexts/LanguageContext';

interface NewConversationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectContact: (contact: ChatContact) => void;
}

const NewConversationDrawer = ({ 
  open, 
  onOpenChange, 
  onSelectContact 
}: NewConversationDrawerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useLanguage();
  const { friendContacts, organizerContacts, loading, searchContacts } = useContactsForChat();

  const { friends: filteredFriends, organizers: filteredOrganizers } = useMemo(() => {
    return searchContacts(searchQuery);
  }, [searchQuery, searchContacts]);

  const handleSelectContact = (contact: ChatContact) => {
    onSelectContact(contact);
    setSearchQuery('');
  };

  const hasNoContacts = friendContacts.length === 0 && organizerContacts.length === 0;
  const hasNoResults = filteredFriends.length === 0 && filteredOrganizers.length === 0 && searchQuery.length > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            {t('notifications.newConversation')}
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('notifications.searchContact')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <ScrollArea className="flex-1 px-4 pb-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : hasNoContacts ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">
                {t('notifications.noContactsAvailable')}
              </p>
              <p className="text-muted-foreground text-xs mt-1">
                {t('notifications.noContactsHint')}
              </p>
            </div>
          ) : hasNoResults ? (
            <div className="text-center py-8">
              <Search className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">
                {t('notifications.noContactsFound')} "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Friends Section */}
              {filteredFriends.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      {t('notifications.tabFriends')}
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {filteredFriends.map((contact) => (
                      <ContactItem
                        key={contact.id}
                        contact={contact}
                        onSelect={handleSelectContact}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Organizers Section */}
              {filteredOrganizers.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      {t('notifications.tabOrganizers')}
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {filteredOrganizers.map((contact) => (
                      <ContactItem
                        key={contact.id}
                        contact={contact}
                        onSelect={handleSelectContact}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
};

interface ContactItemProps {
  contact: ChatContact;
  onSelect: (contact: ChatContact) => void;
}

const ContactItem = ({ contact, onSelect }: ContactItemProps) => {
  return (
    <button
      onClick={() => onSelect(contact)}
      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors text-left"
    >
      <Avatar className="h-10 w-10">
        {contact.avatar_url && (
          <AvatarImage src={contact.avatar_url} alt={contact.name} />
        )}
        <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
          {contact.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{contact.name}</p>
        {contact.subtitle && (
          <p className="text-xs text-muted-foreground truncate">{contact.subtitle}</p>
        )}
      </div>
    </button>
  );
};

export default NewConversationDrawer;
